import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { createRef, forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { Button, Text } from '@beef/ui-kit';
import { DEVICE_NAME_SYMBOL_SIZE, DEVICE_ONE_LINE_NAME_SYMBOL_SIZE } from "../../constants/device";
import { formatCurrency } from "../../utils";
import { DIMENSION_GROUPS, useGetDimensionGroup } from "../../hooks/useGetDimensionGroup";
import { getBadgesLength, getImgMargin, getPreparedStringData } from "./utils";
import { ColorOptions } from "./components/ColorOptions";
import { CmpButton } from "./components/CmpButton";
import { Wrapper } from "./components/Wrapper/Wrapper";
import { DevicePicture } from "./components/DevicePicture";
import { BundlePrice } from "./components/BundlePrice";
import style from "./styles.module.pcss";
import { Favorite } from "./components/Favorite";
import { PromoBadgesList } from "./components/PromoBadges";
export { PromoInstallment } from "./components/PromoInstallment";
var cx = classNames.bind(style);

/* eslint-disable react/prop-types */
export var Device = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _device$name2, _device$colors;
  var extraText = _ref.extraText,
    device = _ref.device,
    className = _ref.className,
    isFetching = _ref.isFetching,
    isError = _ref.isError,
    onOpenModal = _ref.onOpenModal,
    isWithModal = _ref.isWithModal,
    purchaseData = _ref.purchaseData,
    isFavoriteSelected = _ref.isFavoriteSelected,
    favoriteTooltip = _ref.favoriteTooltip,
    setFavorite = _ref.setFavorite;
  var dimension = useGetDimensionGroup();
  var imgRef = /*#__PURE__*/createRef();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDiscount = _useState2[0],
    setIsDiscount = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isDeviceHovered = _useState4[0],
    setIsDeviceHovered = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    price = _useState6[0],
    setPrice = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    oldPrice = _useState8[0],
    setOldPrice = _useState8[1];
  var _useMemo = useMemo(function () {
      var _device$name;
      var name = (_device$name = device === null || device === void 0 ? void 0 : device.name) !== null && _device$name !== void 0 ? _device$name : '';
      return getPreparedStringData(name, device !== null && device !== void 0 && device.isInLine ? DEVICE_ONE_LINE_NAME_SYMBOL_SIZE : DEVICE_NAME_SYMBOL_SIZE, (device === null || device === void 0 ? void 0 : device.isEllipsis) || false);
    }, [device]),
    isLong = _useMemo.isLong,
    deviceName = _useMemo.string;

  // всегда в карточке показываем только один бейдж, если нет флага isSeveralBadges
  var promoBadges = React.useMemo(function () {
    var promoBadgesIsEmptyArray = Array.isArray(device === null || device === void 0 ? void 0 : device.promoBadges) && !device.promoBadges.length;

    // проверка входящих данные на null, undefined, [], {} - по указанной последовательности.
    if (!(device !== null && device !== void 0 && device.promoBadges) || promoBadgesIsEmptyArray || !Object.keys(device === null || device === void 0 ? void 0 : device.promoBadges).length) {
      return [];
    }
    if (device !== null && device !== void 0 && device.isSeveralBadges && Array.isArray(device === null || device === void 0 ? void 0 : device.promoBadges)) {
      return device.promoBadges;
    }
    return Array.isArray(device === null || device === void 0 ? void 0 : device.promoBadges) ? [device.promoBadges[0]] : [device.promoBadges];
  }, [device]);
  var buttonSize = useMemo(function () {
    return dimension === 'mobile' ? 's' : 'm';
  }, [dimension]);

  // Есть только в десктопных карточках (ширина больше чем 240px)
  useEffect(function () {
    if (imgRef) {
      var _imgRef$current;
      var deviceWidth = imgRef === null || imgRef === void 0 ? void 0 : (_imgRef$current = imgRef.current) === null || _imgRef$current === void 0 ? void 0 : _imgRef$current.clientWidth;
      var shouldShowDiscount = !!(deviceWidth && deviceWidth > 240);
      setIsDiscount(shouldShowDiscount);
    }
  }, [imgRef]);

  // FOR SSR
  useEffect(function () {
    if (device !== null && device !== void 0 && device.price) {
      setPrice(formatCurrency(device.price));
    }
    if (device !== null && device !== void 0 && device.oldPrice) {
      setOldPrice(formatCurrency(device.oldPrice));
    }
  }, [device]);

  // TODO: funny useless memoization
  var onPurchaseClick = useCallback(function (id) {
    return function () {
      if (purchaseData) {
        purchaseData.onClick(id);
      }
    };
  }, [purchaseData]);
  var onOpenModalClick = useCallback(function (id, slug) {
    return function (event) {
      event.preventDefault();
      if (onOpenModal) {
        onOpenModal(id, slug);
      }
    };
  }, [onOpenModal]);
  var onMouseEnter = useCallback(function () {
    setIsDeviceHovered(true);
  }, []);
  var onMouseLeave = useCallback(function () {
    setIsDeviceHovered(false);
  }, []);
  var isMobile = dimension === DIMENSION_GROUPS.mobile;
  var promoBadgeSize = isMobile ? 'xs' : 's';
  var highLight = {
    boxShadow: "0 0 0 8px ".concat((device === null || device === void 0 ? void 0 : device.highlight) || 'white'),
    backgroundColor: device === null || device === void 0 ? void 0 : device.highlight,
    borderRadius: '20px 20px 12px 12px'
  };
  var hightLightGradient = {
    background: "linear-gradient(270deg, ".concat((device === null || device === void 0 ? void 0 : device.highlight) || 'white', " 4.17%, rgb(255 255 255 / 0%) 100%)")
  };
  return /*#__PURE__*/React.createElement(Wrapper, {
    isError: !!isError,
    isFetching: !!isFetching
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('wrapper', className),
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    ref: ref,
    style: device !== null && device !== void 0 && device.highlight ? _objectSpread({}, highLight) : undefined
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('img-wrapper'),
    ref: imgRef
  }, !!promoBadges && /*#__PURE__*/React.createElement(PromoBadgesList, {
    promoBadgeSize: promoBadgeSize,
    promoBadges: promoBadges
  }), !!setFavorite && /*#__PURE__*/React.createElement(Favorite, {
    deviceId: device.id,
    isSelected: isFavoriteSelected,
    isVisible: isDeviceHovered,
    setIsSelected: setFavorite,
    tooltip: favoriteTooltip
  }), ((device === null || device === void 0 ? void 0 : device.onCmpClick) && isDeviceHovered || (device === null || device === void 0 ? void 0 : device.isInCmp)) && /*#__PURE__*/React.createElement("div", {
    className: cx('cmpButtonWrap')
  }, /*#__PURE__*/React.createElement(CmpButton, {
    isInCmp: device === null || device === void 0 ? void 0 : device.isInCmp,
    onCmpClick: device === null || device === void 0 ? void 0 : device.onCmpClick,
    tooltipMsg: device === null || device === void 0 ? void 0 : device.cmpTooltipMsg,
    tooltipMsgPos: device.cmpTooltipMsgPos
  })), !!(device !== null && device !== void 0 && device.imgOptions) && /*#__PURE__*/React.createElement("div", {
    className: cx('device-picture'),
    style: device !== null && device !== void 0 && device.isSeveralBadges ? _objectSpread({}, getImgMargin(getBadgesLength(device))) : undefined
  }, /*#__PURE__*/React.createElement(DevicePicture, {
    device: device,
    isWithModal: isWithModal,
    onOpenModal: onOpenModal
  }))), /*#__PURE__*/React.createElement("div", {
    className: cx('description-wrapper', {
      emptyPrice: !price
    })
  }, device !== null && device !== void 0 && device.isBundleView ? /*#__PURE__*/React.createElement(BundlePrice, device) : /*#__PURE__*/React.createElement("div", {
    className: cx('price-wrapper')
  }, price && /*#__PURE__*/React.createElement(Text, {
    size: "size3-b",
    tagType: "p"
  }, price), extraText && /*#__PURE__*/React.createElement("div", {
    className: cx('tariff-description')
  }, /*#__PURE__*/React.createElement(Text, {
    color: "grey60",
    size: "size6-r"
  }, extraText)), oldPrice && /*#__PURE__*/React.createElement("div", {
    className: cx('old-price')
  }, /*#__PURE__*/React.createElement(Text, {
    color: "grey50",
    crossOutColor: "scarlet",
    isCrossedOut: true,
    size: "size6-r"
  }, oldPrice)), !!(device !== null && device !== void 0 && device.discount) && isDiscount && /*#__PURE__*/React.createElement("div", {
    className: cx('discount')
  }, /*#__PURE__*/React.createElement(Text, {
    color: "white",
    size: "size5-m"
  }, "\u2013".concat(device === null || device === void 0 ? void 0 : device.discount, "%")))), (device === null || device === void 0 ? void 0 : device.promoInstallments) && /*#__PURE__*/React.createElement("div", {
    className: cx('promo')
  }, device.promoInstallments), !!(device !== null && device !== void 0 && (_device$name2 = device.name) !== null && _device$name2 !== void 0 && _device$name2.length) && /*#__PURE__*/React.createElement("div", {
    className: cx('name-wrapper', (device === null || device === void 0 ? void 0 : device.isInLine) && 'inline')
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('name')
  }, /*#__PURE__*/React.createElement("a", {
    className: cx('name-link', 'with-cursor'),
    href: device.link,
    onClick: onOpenModalClick(device === null || device === void 0 ? void 0 : device.id, device === null || device === void 0 ? void 0 : device.slug)
  }, /*#__PURE__*/React.createElement(Text, {
    color: "grey60",
    size: "size5-r-s",
    tagType: "p"
  }, deviceName))), isLong && !(device !== null && device !== void 0 && device.isEllipsis) && /*#__PURE__*/React.createElement("div", {
    className: cx('gradient'),
    style: device !== null && device !== void 0 && device.highlight ? _objectSpread({}, hightLightGradient) : undefined
  })), ((device === null || device === void 0 ? void 0 : (_device$colors = device.colors) === null || _device$colors === void 0 ? void 0 : _device$colors.length) || 0) > 0 && /*#__PURE__*/React.createElement("div", {
    className: cx('color-wrapper')
  }, /*#__PURE__*/React.createElement(ColorOptions, {
    colors: (device === null || device === void 0 ? void 0 : device.colors) || [],
    onChangeColor: device === null || device === void 0 ? void 0 : device.onChangeColor
  })), !!purchaseData && /*#__PURE__*/React.createElement("div", {
    className: cx('button-wrapper')
  }, /*#__PURE__*/React.createElement(Button, {
    isLoading: purchaseData.isLoading,
    onClick: onPurchaseClick(device.id),
    size: buttonSize,
    type: "button",
    variant: purchaseData === null || purchaseData === void 0 ? void 0 : purchaseData.variant
  }, purchaseData.title)))));
});